// Body: skeleton rows during load, real rows after.
// Row is React.memo'd so unrelated rows don't re-render when one row toggles.

import { memo, useEffect, useRef } from "react";
import type { JSX, ReactNode } from "react";
import type { ColumnDef, DataTableState } from "./types.js";

const TD_BASE = "py-3 pr-4 text-sm text-slate-700";
const SKELETON_BAR = "h-3 bg-slate-100 rounded animate-pulse";

interface BodyProps<T> {
  columns: ReadonlyArray<ColumnDef<T>>;
  data: readonly T[];
  rowKey: (row: T) => string;
  state: DataTableState;
  selectable: boolean;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  emptyState?: ReactNode;
  highlightId?: string;
}

function alignClass(align: ColumnDef<unknown>["align"]): string {
  if (align === "right") return "text-right";
  if (align === "center") return "text-center";
  return "text-left";
}

function readCell<T>(col: ColumnDef<T>, row: T): ReactNode {
  if (col.cell) return col.cell(row);
  const v = (row as Record<string, unknown>)[String(col.accessorKey)];
  if (v == null) return <span className="text-slate-400">—</span>;
  return String(v);
}

interface RowProps<T> {
  row: T;
  rowId: string;
  columns: ReadonlyArray<ColumnDef<T>>;
  selectable: boolean;
  selected: boolean;
  onToggle: (id: string) => void;
  highlighted?: boolean;
}

function RowImpl<T>({ row, rowId, columns, selectable, selected, onToggle, highlighted }: RowProps<T>): JSX.Element {
  const trRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    if (highlighted) {
      trRef.current?.scrollIntoView?.({ block: "center", behavior: "smooth" });
    }
  }, [highlighted]);
  return (
    <tr ref={trRef} aria-selected={selectable ? selected : undefined}
      className={`border-b border-slate-50 hover:bg-navy-50/60 transition-colors ${selected ? "bg-navy-50/40" : ""} ${highlighted ? "animate-pulse-once" : ""}`}>
      {selectable && (
        <td className={`${TD_BASE} w-8`}>
          <input type="checkbox" checked={selected} aria-label={`Select row ${rowId}`}
            onChange={() => onToggle(rowId)}
            className="rounded border-slate-300 text-navy-700 focus:ring-navy-500" />
        </td>
      )}
      {columns.filter((c) => !c.hidden).map((c) => (
        <td key={String(c.accessorKey)} className={`${TD_BASE} ${alignClass(c.align)}`}>
          {readCell(c, row)}
        </td>
      ))}
    </tr>
  );
}

const MemoRow = memo(RowImpl) as typeof RowImpl;

function SkeletonRow({ cols }: { cols: number }): JSX.Element {
  return (
    <tr className="border-b border-slate-50" aria-hidden="true">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className={TD_BASE}><div className={SKELETON_BAR} style={{ width: `${40 + ((i * 17) % 50)}%` }} /></td>
      ))}
    </tr>
  );
}

function LoadingRows({ cols, rows }: { cols: number; rows: number }): JSX.Element {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => <SkeletonRow key={i} cols={cols} />)}
    </>
  );
}

function MessageRow({ colSpan, children, role }: { colSpan: number; children: ReactNode; role?: "alert" }): JSX.Element {
  return (
    <tr>
      <td colSpan={colSpan} className="py-10 text-center text-sm text-slate-500" role={role}>
        {children}
      </td>
    </tr>
  );
}

export function DataTableBody<T>(props: BodyProps<T>): JSX.Element {
  const { columns, data, rowKey, state, selectable, isLoading, isError, errorMessage, emptyState, highlightId } = props;
  const visibleCols = columns.filter((c) => !c.hidden).length + (selectable ? 1 : 0);
  if (isError) {
    return (
      <tbody>
        <MessageRow colSpan={visibleCols} role="alert">
          <span className="text-red-600">{errorMessage ?? "Failed to load data."}</span>
        </MessageRow>
      </tbody>
    );
  }
  if (isLoading) {
    return <tbody aria-busy="true"><LoadingRows cols={visibleCols} rows={Math.min(state.query.limit, 8)} /></tbody>;
  }
  if (data.length === 0) {
    return <tbody><MessageRow colSpan={visibleCols}>{emptyState ?? "No results."}</MessageRow></tbody>;
  }
  return (
    <tbody>
      {data.map((row) => {
        const id = rowKey(row);
        return (
          <MemoRow key={id} row={row} rowId={id} columns={columns} selectable={selectable}
            selected={state.selection.selected.has(id)} onToggle={state.selection.toggle}
            highlighted={highlightId === id} />
        );
      })}
    </tbody>
  );
}
